<template>
  <div id="pagination">
    <div class="paginate" v-if="(totalPages > 1)">
      <button
        type="button"
        class="paginate-btn"
        :class="{ disabled: (page <= 1) }"
        @click="() => onPageChange(page - 1)"
      >
        <div class="item">
          <font-awesome-icon :icon="faChevronLeft" />
          <span>{{ $t('PREV') }}</span>
        </div>
      </button>

      <template v-for="(x, i) in [...Array(page - 1)]">
        <button v-if="((i === page - 4) && (i > 0))" type="button" :key="`pre-dots-${i}`" class="paginate-num passive">…</button>
        <template v-if="((i < page - 4) && (i !== 0))" />
        <button v-else type="button" :key="`pre-${i}`" @click="() => onPageChange(i + 1)" class="paginate-num">{{ i + 1 }}</button>
      </template>

      <button type="button" class="paginate-num active">{{ page }}</button>

      <template v-for="(x, i) in [...Array((totalPages - page >= 0) ? (totalPages - page) : 0)]">
        <button v-if="((i === 2) && (i !== totalPages - page - 1))" type="button" :key="`next-dots-${i}`" class="paginate-num passive">…</button>
        <template v-if="((i > 2) && (i !== totalPages - page - 1))" />
        <button v-else type="button" :key="`next-${i}`" @click="() => onPageChange(i + page + 1)" class="paginate-num">{{ i + page + 1 }}</button>
      </template>

      <button
        type="button"
        class="paginate-btn"
        :class="{ disabled: (page >= totalPages) }"
        @click="() => onPageChange(page + 1)"
      >
        <div class="item">
          <span>{{ $t('NEXT') }}</span>
          <font-awesome-icon :icon="faChevronRight" />
        </div>
      </button>

      <span> | </span>
    </div>

    <div class="per-page" :class="{ standalone: (totalPages <= 1) }">
      <span>{{ $t('SHOWING') }}</span>
      <select :value="pageSize" @change="(e) => onPageSizeChange(+e.target.value)">
        <option v-for="x in pageSizeOptions" :key="x" :value="x">{{ x }}</option>
      </select>
      <span>{{ $t('PER_PAGE') }}</span>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

export default {
  name: 'Pagination',
  components: {
    FontAwesomeIcon
  },
  props: {
    page: {
      type: Number,
      required: true
    },
    pageSize: {
      type: Number,
      required: true
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 20, 50],
    },
    total: {
      type: Number,
      default: 1
    }
  },
  setup(props, { emit }) {
    const totalPages = computed(() => Math.ceil(props.total / props.pageSize));

    return {
      totalPages,
      faChevronLeft,
      faChevronRight,
      onPageChange(val) {
        emit('pageChange', val);
      },
      onPageSizeChange(val) {
        emit('pageSizeChange', val);
      }
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#pagination {
  width: 100%;
  display: block;
  font-family: "Source Sans Pro";
  font-size: 12px;
  text-align: right;

  .paginate {
    display: inline-block;

    .paginate-btn {
      border: 0;
      padding: 7px 15px;
      color: $lavenderGrey;
      margin-left: 10px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: all .2s ease-out;
      vertical-align: middle;
      background: none;

      .item {
        position: relative;
        z-index: 2;

        svg, span {
          vertical-align: middle;
        }

        svg {
          width: 16px;
        }

        span {
          padding-left: 7px;
          padding-right: 7px;
        }
      }

      &:hover {
        color: $geranium;
      }

      &:focus {
        outline: none;
      }

      &.disabled {
        width: 0;
        height: 0;
        padding: 0;
        color: $alto;
      }
    }

    .paginate-num {
      color: $lavenderGrey;
      padding: 3px 0;
      padding-top: 2px;
      text-align: center;
      width: 24px;
      height: 24px;
      background: none;
      border: 0;
      border-radius: 50%;
      vertical-align: middle;
      cursor: pointer;
      margin-left: 10px;
      transition: all .2s ease-out;
      font-weight: bold;

      &.active {
        background-color: $independence;
        color: $babyPowder;
        cursor: default;
      }

      &.passive {
        cursor: default;
        color: $lavenderGrey;
        margin: 0;
        margin-right: -15px;
      }

      &:not(.passive):not(.active):hover {
        background-color: $babyPowder;
        color: $independence;
      }

      &:focus {
        outline: none;
      }
    }
  }

  .per-page {
    display: inline-block;
    margin-left: 15px;

    &.standalone {
      margin-left: 0;
    }

    span {
      vertical-align: middle;
      padding-left: 5px;
      padding-right: 5px;
    }

    select {
      border: 0;
      border-radius: 4px;
      padding: 7px 15px;
      font-family: "Source Sans Pro";
      color: $independence;

      &:focus {
        outline: none;
      }

      option {
        border: 0;
        border-radius: 4px;

        &:focus {
          outline: none;
        }
      }
    }
  }
}
</style>
